import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';

export default function AdminCategories() {
  const [categories,    setCategories]    = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [tab,           setTab]           = useState('categories');
  const [modal,         setModal]         = useState(null); // null | 'addCat' | 'editCat' | 'addSub' | 'editSub'
  const [editing,       setEditing]       = useState(null);
  const [form,          setForm]          = useState({});
  const [imageFile,     setImageFile]     = useState(null);
  const [saving,        setSaving]        = useState(false);

  const loadAll = () => {
    setLoading(true);
    Promise.all([api.get('/categories/admin'), api.get('/subcategories/admin')])
      .then(([c, s]) => { setCategories(c.data.data || []); setSubcategories(s.data.data || []); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadAll(); }, []);

  const openCatModal = (cat = null) => {
    setEditing(cat);
    setForm(cat ? { name: cat.name, description: cat.description || '', isActive: cat.isActive, sortOrder: cat.sortOrder } : { name: '', description: '', isActive: true, sortOrder: 0 });
    setImageFile(null);
    setModal(cat ? 'editCat' : 'addCat');
  };

  const openSubModal = (sub = null) => {
    setEditing(sub);
    setForm(sub ? { name: sub.name, description: sub.description || '', category: sub.category?._id || '', isActive: sub.isActive, sortOrder: sub.sortOrder } : { name: '', description: '', category: '', isActive: true, sortOrder: 0 });
    setModal(sub ? 'editSub' : 'addSub');
  };

  const saveCat = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('image', imageFile);
      if (editing) {
        const r = await api.put(`/categories/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setCategories(cs => cs.map(c => c._id === editing._id ? r.data.data : c));
        toast.success('Category updated!');
      } else {
        const r = await api.post('/categories', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setCategories(cs => [...cs, r.data.data]);
        toast.success('Category created!');
      }
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const deleteCat = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await api.delete(`/categories/${id}`); setCategories(cs => cs.filter(c => c._id !== id)); toast.success('Deleted.'); }
    catch { toast.error('Failed to delete.'); }
  };

  const saveSub = async () => {
    if (!form.name || !form.category) { toast.error('Name and category required'); return; }
    setSaving(true);
    try {
      if (editing) {
        const r = await api.put(`/subcategories/${editing._id}`, form);
        setSubcategories(ss => ss.map(s => s._id === editing._id ? r.data.data : s));
        toast.success('Subcategory updated!');
      } else {
        const r = await api.post('/subcategories', form);
        setSubcategories(ss => [...ss, r.data.data]);
        toast.success('Subcategory created!');
      }
      setModal(null);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed.'); }
    finally { setSaving(false); }
  };

  const deleteSub = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    try { await api.delete(`/subcategories/${id}`); setSubcategories(ss => ss.filter(s => s._id !== id)); toast.success('Deleted.'); }
    catch { toast.error('Failed.'); }
  };

  const h = e => setForm(f => ({ ...f, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-headline font-black text-3xl uppercase tracking-tight">Categories</h1>
          <p className="font-body text-sm text-secondary mt-1">Manage product categories and subcategories.</p>
        </div>
        <button onClick={() => tab === 'categories' ? openCatModal() : openSubModal()} className="btn-primary">
          <span className="material-symbols-outlined">add</span>
          Add {tab === 'categories' ? 'Category' : 'Subcategory'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-outline-variant/30 mb-6">
        {['categories','subcategories'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-6 py-3 font-headline font-bold text-sm uppercase tracking-tight border-b-2 -mb-px transition-colors ${
              tab === t ? 'border-primary-container text-primary-container' : 'border-transparent text-secondary hover:text-on-surface'
            }`}>{t}</button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_,i) => <div key={i} className="h-14 skeleton" />)}</div>
      ) : tab === 'categories' ? (
        <div className="bg-white border border-outline-variant/20 overflow-hidden">
          <table className="cf-table">
            <thead><tr><th>Image</th><th>Name</th><th>Slug</th><th>Status</th><th>Order</th><th>Actions</th></tr></thead>
            <tbody>
              {categories.map(cat => (
                <tr key={cat._id}>
                  <td>
                    {cat.image
                      ? <img src={cat.image} alt={cat.name} className="w-10 h-10 object-cover" />
                      : <div className="w-10 h-10 bg-surface-container flex items-center justify-center">
                          <span className="material-symbols-outlined text-[18px] text-outline-variant">image</span>
                        </div>
                    }
                  </td>
                  <td className="font-bold">{cat.name}</td>
                  <td className="text-secondary text-xs font-mono">{cat.slug}</td>
                  <td><span className={`badge ${cat.isActive ? 'badge-green' : 'badge-red'}`}>{cat.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>{cat.sortOrder}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openCatModal(cat)} className="p-1.5 text-secondary hover:text-primary-container transition-colors">
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                      </button>
                      <button onClick={() => deleteCat(cat._id)} className="p-1.5 text-secondary hover:text-error transition-colors">
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {categories.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-secondary">No categories yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-outline-variant/20 overflow-hidden">
          <table className="cf-table">
            <thead><tr><th>Name</th><th>Category</th><th>Status</th><th>Order</th><th>Actions</th></tr></thead>
            <tbody>
              {subcategories.map(sub => (
                <tr key={sub._id}>
                  <td className="font-bold">{sub.name}</td>
                  <td className="text-secondary">{sub.category?.name || '—'}</td>
                  <td><span className={`badge ${sub.isActive ? 'badge-green' : 'badge-red'}`}>{sub.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td>{sub.sortOrder}</td>
                  <td>
                    <div className="flex gap-2">
                      <button onClick={() => openSubModal(sub)} className="p-1.5 text-secondary hover:text-primary-container transition-colors">
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                      </button>
                      <button onClick={() => deleteSub(sub._id)} className="p-1.5 text-secondary hover:text-error transition-colors">
                        <span className="material-symbols-outlined text-[18px]">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {subcategories.length === 0 && (
                <tr><td colSpan={5} className="text-center py-8 text-secondary">No subcategories yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Category Modal */}
      {(modal === 'addCat' || modal === 'editCat') && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container">
              <h2 className="font-headline font-black text-xl uppercase">{editing ? 'Edit Category' : 'Add Category'}</h2>
              <button onClick={() => setModal(null)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="input-label">Category Name *</label>
                <input name="name" value={form.name || ''} onChange={h} className="input-field" placeholder="e.g. Bolts" />
              </div>
              <div>
                <label className="input-label">Description</label>
                <textarea name="description" value={form.description || ''} onChange={h} rows={2} className="input-field resize-none" />
              </div>
              <div>
                <label className="input-label">Category Image</label>
                <input type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} className="input-field" />
                {editing?.image && <img src={editing.image} alt="" className="mt-2 h-20 object-contain" />}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Sort Order</label>
                  <input name="sortOrder" type="number" value={form.sortOrder || 0} onChange={h} className="input-field" />
                </div>
                <div className="flex items-end pb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isActive" checked={!!form.isActive} onChange={h} className="w-4 h-4 accent-primary-container" />
                    <span className="font-label text-sm font-bold uppercase tracking-wider">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveCat} disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {(modal === 'addSub' || modal === 'editSub') && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-container">
              <h2 className="font-headline font-black text-xl uppercase">{editing ? 'Edit Subcategory' : 'Add Subcategory'}</h2>
              <button onClick={() => setModal(null)}><span className="material-symbols-outlined">close</span></button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="input-label">Parent Category *</label>
                <select name="category" value={form.category || ''} onChange={h} className="input-field">
                  <option value="">Select category...</option>
                  {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="input-label">Subcategory Name *</label>
                <input name="name" value={form.name || ''} onChange={h} className="input-field" placeholder="e.g. Hex Bolts" />
              </div>
              <div>
                <label className="input-label">Description</label>
                <textarea name="description" value={form.description || ''} onChange={h} rows={2} className="input-field resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Sort Order</label>
                  <input name="sortOrder" type="number" value={form.sortOrder || 0} onChange={h} className="input-field" />
                </div>
                <div className="flex items-end pb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" name="isActive" checked={!!form.isActive} onChange={h} className="w-4 h-4 accent-primary-container" />
                    <span className="font-label text-sm font-bold uppercase tracking-wider">Active</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-container flex justify-end gap-3">
              <button onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveSub} disabled={saving} className="btn-primary">{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
